// src/components/GroupMemberManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    IconButton,
    InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../auth/AuthContext'; // Assuming you have this context for dbUser
import { useNotification } from '../notification/NotificationProvider';

const GroupMemberManager = ({ initialMembers = [], pendingMembers = [], onMembersChange, groupId, isAdmin }) => {
    const { dbUser, isLoading: authLoading } = useAuth();
    const { triggerNotification } = useNotification();
    const [currentMembers, setCurrentMembers] = useState(initialMembers);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');

    // Ensure the initialMembers are properly set when the component mounts or initialMembers change
    useEffect(() => {
        console.log('Members is ADmin?¡', isAdmin)
        setCurrentMembers(initialMembers);
    }, [initialMembers]);

    // Function to fetch user details for initial members if they are just UIDs
    useEffect(() => {
        const fetchInitialMemberDetails = async () => {
            if (initialMembers.length > 0 && dbUser) {
                // Assuming initialMembers might contain UIDs and you need their emails for display
                // If initialMembers already contain emails, you can skip this.
                const membersWithDetails = initialMembers
                setCurrentMembers(membersWithDetails.filter(Boolean)); // Filter out any null/undefined from failed fetches
            }
        };
        fetchInitialMemberDetails();
    }, [initialMembers, dbUser]);


    const handleSearch = useCallback(async () => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setSearchError('');
            return;
        }

        setLoadingSearch(true);
        setSearchError('');
        try {
            // This is the function you mentioned: dbUser.getPublicUsers(email:searchPartialEmail)
            const response = (await dbUser.getPublicUsers({
                pageNum: 1,
                searchTerm: searchTerm,
                limit: 10
            })).users
            // if (response.success && Array.isArray(response.users)) {
            //     // Filter out users who are already members
            //     const filteredResults = response.users.filter(
            //         (user) => !currentMembers.includes(user.email)
            //     );
            //     
            console.log('Hello i am filtering')
            setSearchResults(response);
            // } else {
            //     setSearchResults([]);
            //     setSearchError(response.message || 'No se encontraron usuarios.');
            // }
        } catch (error) {
            console.error('Error searching for users:', error);
            setSearchError('Error al buscar usuarios. Inténtalo de nuevo.');
        } finally {
            setLoadingSearch(false);
        }
    }, [searchTerm, currentMembers, dbUser]);

    // Debounce the search input to avoid excessive API calls
    useEffect(() => {
        const handler = setTimeout(() => {
            handleSearch();
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm, handleSearch]);

    const handleAddMember = async (user) => {
        if (!isAdmin && groupId) {
            setActionError('Solo los administradores pueden añadir miembros.');
            return;
        }
        console.log('handle add user', user)
        if (currentMembers.flatMap(m => m.email).includes(user.email)) {
            setActionError('Este usuario ya es miembro del grupo.');
            return;
        }
        const updatedMembers = [...currentMembers, { email: user.email }];

        const realresponse = await dbUser.updateGroupMembers(groupId, { members: updatedMembers.flatMap(m => m.email) });
        triggerNotification({
            message: `Solicitud de unirse al grupo enviada a ${user.email}`,
            sound: true,
            show: true,
            type: 'tiny-top',
            cat: 'received-request-from-group',
            forceId: 'group-member-manager-handle-add-member-success'
        });
        await dbUser.update();
        console.log('realresponse?', realresponse)

        setCurrentMembers(updatedMembers);
        onMembersChange(updatedMembers); // Notify parent component
        setSearchTerm(''); // Clear search input
        setSearchResults([]); // Clear search results
        setActionSuccess(`Miembro ${user.email} añadido.`);
        setActionError('');
    };

    const handleRemoveMember = async (memberToRemove) => {
        if (!isAdmin) {
            setActionError('Solo los administradores pueden eliminar miembros.');
            return;
        }
        // Prevent removing the group owner if necessary (you'll need to pass ownerId)
        // For simplicity, let's assume the owner cannot be removed for now.
        // If owner is passed as a prop, you can check: if (memberToRemove === ownerEmail) { /* error */ }

        const updatedMembers = currentMembers.filter((member) => member !== memberToRemove);
        const realresponse = await dbUser.updateGroupMembersRemoveOne(groupId, { member: memberToRemove });
        triggerNotification({
            message: `El usuario ${memberToRemove} ya no forma parte de este grupo`,
            sound: true,
            show: true,
            type: 'tiny-top',
            cat: 'received-request-from-group',
            forceId: 'group-member-manager-handle-remove-member-success'
        });
        await dbUser.update();
        console.log('memberToRemove?', memberToRemove)
        if (realresponse) {
            setCurrentMembers(updatedMembers);
            onMembersChange(updatedMembers); // Notify parent component
            setActionSuccess(`Miembro ${memberToRemove} eliminado.`);
            setActionError('');
        } else {
            console.log('FAILED TO UPDATE INTERFACE LIST')
        }

    };

    if (authLoading) {
        return <CircularProgress />;
    }

    return (
        <Box sx={{ mt: 3, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: 'white', boxShadow: 3 }}>
            <Typography variant="h6" gutterBottom>Gestionar Miembros del Grupo</Typography>

            {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}
            {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

            <TextField
                label="Buscar usuarios por email"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {loadingSearch ? <CircularProgress size={20} /> : <SearchIcon />}
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
                disabled={!isAdmin && groupId}
            />

            {searchError && <Alert severity="warning" sx={{ mb: 2 }}>{searchError}</Alert>}

            {searchResults.length > 0 && (
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, mb: 2 }}>
                    <List dense>
                        {searchResults.map((user) => (
                            <ListItem
                                key={user.uid} // Assuming user object from search has a uid
                                secondaryAction={
                                    <IconButton edge="end" aria-label="add" onClick={() => handleAddMember(user)} disabled={!isAdmin && groupId}>
                                        <AddIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText primary={user.email} secondary={user.displayName || user.uid} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Miembros Actuales:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentMembers.length === 0 && <Typography variant="body2" color="text.secondary">No hay miembros en este grupo.</Typography>}
                    {currentMembers.map((member, index) => (
                        <Chip
                            key={member.email || index}
                            label={member.email}
                            onDelete={() => { handleRemoveMember(member.email) }}
                            color="primary"
                            variant="outlined"
                            deleteIcon={<DeleteIcon />}
                            disabled={groupId && !isAdmin}
                        />
                    ))}
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Pendientes:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {pendingMembers.length === 0 && <Typography variant="body2" color="text.secondary">No hay miembros pendinetes en este grupo.</Typography>}
                    {pendingMembers.map((member, index) => (
                        <Chip
                            key={member.email || index}
                            label={member.email}
                            onDelete={() => { handleRemoveMember(member.email) }}
                            color="primary"
                            variant="outlined"
                            deleteIcon={<DeleteIcon />}
                            disabled={groupId}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default GroupMemberManager;